import React, { useState, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import { apiService } from '../services/apiService'
import { formatCurrency } from '../utils/formatters'
import {
  CreditCardIcon,
  TruckIcon,
  UserIcon,
  CalendarIcon,
  SparklesIcon,
  ChartBarIcon,
  ClockIcon,
  CpuChipIcon,
  DocumentTextIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'

const QuotesPage = () => {
  const [loading, setLoading] = useState(false)
  const [calculating, setCalculating] = useState(false)
  const [clients, setClients] = useState([])
  const [vehicles, setVehicles] = useState([])
  const [quote, setQuote] = useState(null)

  const [formData, setFormData] = useState({
    clientId: '',
    vehicleId: '',
    startDate: '',
    endDate: '',
    includeInsurance: true,
    includeMaintenanceService: true,
    urgency: 'normal',
    clientScore: 0,
    observations: ''
  })

  // Carregar dados ao montar
  useEffect(() => {
    loadInitialData()
  }, [])

  const loadInitialData = async () => {
    try {
      setLoading(true)
      const [clientsData, vehiclesData] = await Promise.all([
        apiService.getClients(),
        apiService.getVehicles()
      ])
      setClients(clientsData.clients || [])
      setVehicles(vehiclesData.vehicles || [])
    } catch (error) {
      toast.error('Erro ao carregar dados')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  // Atualizar score do cliente ao selecionar
  useEffect(() => {
    if (formData.clientId) {
      const client = clients.find(c => c.id === formData.clientId)
      if (client) {
        setFormData(prev => ({ ...prev, clientScore: client.score || 0 }))
      }
    }
  }, [formData.clientId, clients])

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const calculateDays = () => {
    if (!formData.startDate || !formData.endDate) return 0
    const start = new Date(formData.startDate)
    const end = new Date(formData.endDate)
    const diffTime = Math.abs(end - start)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const handleCalculateQuote = async () => {
    // Validações
    if (!formData.clientId || !formData.vehicleId || !formData.startDate || !formData.endDate) {
      toast.error('Preencha todos os campos obrigatórios')
      return
    }

    const days = calculateDays()
    if (days <= 0) {
      toast.error('A data de fim deve ser posterior à data de início')
      return
    }

    setCalculating(true)

    try {
      const vehicle = vehicles.find(v => v.id === formData.vehicleId)
      const client = clients.find(c => c.id === formData.clientId)

      const baseRate = parseFloat(vehicle?.monthly_rate || 0)

      // Fatores de ajuste baseados em IA
      let scoreDiscount = 0
      if (client.score >= 90) scoreDiscount = 15
      else if (client.score >= 80) scoreDiscount = 10
      else if (client.score >= 70) scoreDiscount = 5

      let urgencyMultiplier = 1.0
      if (formData.urgency === 'urgent') urgencyMultiplier = 1.2
      else if (formData.urgency === 'immediate') urgencyMultiplier = 1.35

      let periodDiscount = 0
      if (days >= 90) periodDiscount = 15
      else if (days >= 60) periodDiscount = 10
      else if (days >= 30) periodDiscount = 5

      // Cálculo base
      const subtotal = baseRate * days
      const urgencyCost = subtotal * (urgencyMultiplier - 1)

      // Descontos e custos adicionais
      const periodDiscountAmount = subtotal * (periodDiscount / 100)
      const scoreDiscountAmount = subtotal * (scoreDiscount / 100)
      const insuranceCost = formData.includeInsurance ? (subtotal * 0.08) : 0
      const maintenanceCost = formData.includeMaintenanceService ? (subtotal * 0.05) : 0

      const totalDiscount = periodDiscountAmount + scoreDiscountAmount
      const totalDiscountPercentage = ((totalDiscount / subtotal) * 100).toFixed(2)

      const finalValue = subtotal + urgencyCost + insuranceCost + maintenanceCost - totalDiscount

      // Criar cotação no backend
      const quotationData = {
        client_id: formData.clientId,
        vehicle_id: formData.vehicleId,
        start_date: formData.startDate,
        end_date: formData.endDate,
        monthly_rate: baseRate,
        discount_percentage: parseFloat(totalDiscountPercentage),
        notes: formData.observations || `Urgência: ${formData.urgency}. Seguro: ${formData.includeInsurance ? 'Sim' : 'Não'}. Manutenção: ${formData.includeMaintenanceService ? 'Sim' : 'Não'}`,
        validity_days: 7
      }

      const response = await apiService.createQuotation(quotationData)

      const quoteResult = {
        id: response.quotation?.id,
        clientName: client.name,
        vehicleModel: `${vehicle.brand} ${vehicle.model}`,
        vehiclePlate: vehicle.license_plate,
        days,
        baseRate,
        subtotal,
        urgencyMultiplier,
        urgencyCost,
        periodDiscount: periodDiscount / 100,
        periodDiscountAmount,
        scoreDiscount: scoreDiscount / 100,
        scoreDiscountAmount,
        insuranceCost,
        maintenanceCost,
        total: finalValue,
        validUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('pt-BR'),
        aiRecommendations: generateAIRecommendations(client, vehicle, days, finalValue)
      }

      setQuote(quoteResult)
      toast.success('Cotação criada e salva com sucesso!')

    } catch (error) {
      toast.error('Erro ao criar cotação')
      console.error(error)
    } finally {
      setCalculating(false)
    }
  }

  const generateAIRecommendations = (client, vehicle, days, total) => {
    const recommendations = []

    if (client.score >= 85) {
      recommendations.push({
        type: 'positive',
        text: `Cliente com excelente histórico (score ${client.score}). Considere oferecer condições especiais de pagamento.`
      })
    } else if (client.score < 70) {
      recommendations.push({
        type: 'warning',
        text: `Cliente com score ${client.score}. Recomendamos exigir depósito maior e seguro obrigatório.`
      })
    }

    if (days >= 60) {
      recommendations.push({
        type: 'info',
        text: 'Locação de longo prazo. Considere incluir plano de manutenção preventiva no contrato.'
      })
    }

    if (total > 50000) {
      recommendations.push({
        type: 'info',
        text: 'Valor elevado. Sugestão: dividir pagamento em parcelas quinzenais para melhor fluxo de caixa.'
      })
    }

    if (vehicle.maintenance_score < 80) {
      recommendations.push({
        type: 'warning',
        text: `Veículo com score de manutenção ${vehicle.maintenance_score}%. Recomenda-se revisão antes da locação.`
      })
    }

    return recommendations
  }

  const handleResetForm = () => {
    setFormData({
      clientId: '',
      vehicleId: '',
      startDate: '',
      endDate: '',
      includeInsurance: true,
      includeMaintenanceService: true,
      urgency: 'normal',
      clientScore: 0,
      observations: ''
    })
    setQuote(null)
  }

  const handleGenerateContract = () => {
    toast.success('Redirecionando para criação de contrato...')
    // Aqui você pode redirecionar para a página de contratos com os dados preenchidos
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando dados...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
            <SparklesIcon className="w-8 h-8 text-purple-600" />
            Cotação Automática
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Sistema inteligente de cotação com análise preditiva de IA
          </p>
        </div>

        <div className="flex items-center gap-2 bg-purple-50 dark:bg-purple-900/30 px-4 py-2 rounded-lg">
          <CpuChipIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          <span className="text-sm font-medium text-purple-700 dark:text-purple-300">IA Ativada</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Formulário */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <DocumentTextIcon className="w-6 h-6 text-blue-600" />
              Dados da Cotação
            </h2>

            <div className="space-y-4">
              {/* Cliente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <UserIcon className="w-4 h-4 inline mr-1" />
                  Cliente *
                </label>
                <select
                  name="clientId"
                  value={formData.clientId}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                >
                  <option value="">Selecione um cliente</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name} - Score: {client.score || 0}
                    </option>
                  ))}
                </select>
              </div>

              {/* Veículo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <TruckIcon className="w-4 h-4 inline mr-1" />
                  Veículo *
                </label>
                <select
                  name="vehicleId"
                  value={formData.vehicleId}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  required
                >
                  <option value="">Selecione um veículo</option>
                  {vehicles.filter(v => v.status === 'available').map(vehicle => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.brand} {vehicle.model} - {vehicle.license_plate} - R$ {vehicle.monthly_rate}/mês
                    </option>
                  ))}
                </select>
              </div>

              {/* Datas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <CalendarIcon className="w-4 h-4 inline mr-1" />
                    Data Início *
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <CalendarIcon className="w-4 h-4 inline mr-1" />
                    Data Fim *
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>
              </div>

              {/* Urgência */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <ClockIcon className="w-4 h-4 inline mr-1" />
                  Urgência
                </label>
                <select
                  name="urgency"
                  value={formData.urgency}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  <option value="normal">Normal (entrega em 3-5 dias)</option>
                  <option value="urgent">Urgente (entrega em 1-2 dias) +20%</option>
                  <option value="immediate">Imediato (entrega hoje) +35%</option>
                </select>
              </div>

              {/* Serviços Adicionais */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Serviços Adicionais
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="includeInsurance"
                    checked={formData.includeInsurance}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Incluir Seguro Total (+8% do valor)
                  </span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="includeMaintenanceService"
                    checked={formData.includeMaintenanceService}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Incluir Serviço de Manutenção (+5% do valor)
                  </span>
                </label>
              </div>

              {/* Observações */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Observações
                </label>
                <textarea
                  name="observations"
                  value={formData.observations}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Adicione observações sobre esta cotação..."
                />
              </div>

              {/* Botões */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleCalculateQuote}
                  disabled={calculating}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {calculating ? (
                    <>
                      <ArrowPathIcon className="w-5 h-5 animate-spin" />
                      Calculando...
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="w-5 h-5" />
                      Calcular Cotação
                    </>
                  )}
                </button>

                <button
                  onClick={handleResetForm}
                  className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
                >
                  Limpar
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Resultado da Cotação */}
        <div className="lg:col-span-1">
          {quote ? (
            <div className="space-y-4">
              {/* Resumo */}
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
                <h3 className="text-lg font-semibold mb-4">Cotação Gerada</h3>

                <div className="space-y-3">
                  <div>
                    <p className="text-sm opacity-80">Cliente</p>
                    <p className="font-semibold">{quote.clientName}</p>
                  </div>

                  <div>
                    <p className="text-sm opacity-80">Veículo</p>
                    <p className="font-semibold">{quote.vehicleModel}</p>
                    <p className="text-xs opacity-70">{quote.vehiclePlate}</p>
                  </div>

                  <div className="border-t border-white/20 pt-3 mt-3">
                    <p className="text-sm opacity-80">Período</p>
                    <p className="font-semibold">{quote.days} dias</p>
                  </div>

                  <div className="border-t border-white/20 pt-3 mt-3">
                    <p className="text-2xl font-bold">R$ {quote.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                    <p className="text-xs opacity-70">Válido até {quote.validUntil}</p>
                  </div>
                </div>
              </div>

              {/* Detalhamento */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Detalhamento</h4>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Subtotal ({quote.days} dias × R$ {quote.baseRate})</span>
                    <span className="font-medium">{formatCurrency(quote.subtotal)}</span>
                  </div>

                  {quote.urgencyCost > 0 && (
                    <div className="flex justify-between text-orange-600">
                      <span>Taxa de Urgência</span>
                      <span>+ {formatCurrency(quote.urgencyCost)}</span>
                    </div>
                  )}

                  {quote.insuranceCost > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Seguro Total</span>
                      <span className="font-medium">+ {formatCurrency(quote.insuranceCost)}</span>
                    </div>
                  )}

                  {quote.maintenanceCost > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Serviço de Manutenção</span>
                      <span className="font-medium">+ {formatCurrency(quote.maintenanceCost)}</span>
                    </div>
                  )}

                  {quote.periodDiscountAmount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Desconto por Período ({(quote.periodDiscount * 100).toFixed(0)}%)</span>
                      <span>- {formatCurrency(quote.periodDiscountAmount)}</span>
                    </div>
                  )}

                  {quote.scoreDiscountAmount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Desconto Score Cliente ({(quote.scoreDiscount * 100).toFixed(0)}%)</span>
                      <span>- {formatCurrency(quote.scoreDiscountAmount)}</span>
                    </div>
                  )}

                  <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-2 flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <span className="text-blue-600 dark:text-blue-400">R$ {quote.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>

                <button
                  onClick={handleGenerateContract}
                  className="w-full mt-4 bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-all duration-200"
                >
                  Gerar Contrato
                </button>
              </div>

              {/* Recomendações da IA */}
              {quote.aiRecommendations.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <CpuChipIcon className="w-5 h-5 text-purple-600" />
                    Recomendações da IA
                  </h4>

                  <div className="space-y-3">
                    {quote.aiRecommendations.map((rec, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-lg text-sm ${
                          rec.type === 'positive' ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300' :
                          rec.type === 'warning' ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300' :
                          'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300'
                        }`}
                      >
                        {rec.text}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
              <ChartBarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                Preencha os dados e clique em "Calcular Cotação" para gerar uma proposta inteligente
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default QuotesPage